import { Box, Typography } from "@mui/material";
import { ReactNode } from "react";

function renderInline(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const pattern = /\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = pattern.exec(text))) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    if (match[1] !== undefined) {
      nodes.push(
        <Box component="strong" key={`b-${key++}`} sx={{ fontWeight: 700 }}>
          {match[1]}
        </Box>
      );
    } else if (match[2] !== undefined) {
      nodes.push(
        <Box component="em" key={`i-${key++}`} sx={{ fontStyle: "italic" }}>
          {match[2]}
        </Box>
      );
    } else if (match[3] !== undefined) {
      nodes.push(
        <Box
          component="code"
          key={`c-${key++}`}
          sx={{
            fontFamily: "monospace",
            fontSize: "0.9em",
            px: 0.5,
            py: 0.1,
            borderRadius: 0.5,
            bgcolor: "action.hover",
          }}
        >
          {match[3]}
        </Box>
      );
    }
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes;
}

function isBullet(line: string) {
  return /^\s*[-*]\s+/.test(line);
}

function isNumbered(line: string) {
  return /^\s*\d+\.\s+/.test(line);
}

function listItemText(line: string) {
  return line.replace(/^\s*(?:[-*]|\d+\.)\s+/, "");
}

function renderBlock(block: string, blockIndex: number) {
  const lines = block.split("\n").filter((line) => line.trim().length > 0);
  if (!lines.length) {
    return null;
  }

  const allBullets = lines.every(isBullet);
  const allNumbered = lines.every(isNumbered);

  if (allBullets || allNumbered) {
    return (
      <Box
        key={`block-${blockIndex}`}
        component={allNumbered ? "ol" : "ul"}
        sx={{
          m: 0,
          pl: 2.5,
          fontSize: "inherit",
          color: "inherit",
          "& li + li": { mt: 0.5 },
        }}
      >
        {lines.map((line, index) => (
          <Box component="li" key={`li-${blockIndex}-${index}`}>
            {renderInline(listItemText(line))}
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Typography
      key={`block-${blockIndex}`}
      variant="body2"
      component="p"
      sx={{ m: 0, fontSize: "inherit", color: "inherit" }}
    >
      {lines.map((line, index) => (
        <Box component="span" key={`line-${blockIndex}-${index}`}>
          {index > 0 ? <br /> : null}
          {renderInline(line)}
        </Box>
      ))}
    </Typography>
  );
}

export default function AssistanceMarkdown({ text }: { text: string }) {
  const blocks = text.replace(/\r\n/g, "\n").trim().split(/\n{2,}/);

  return (
    <Box
      sx={{
        fontSize: "0.8125rem",
        lineHeight: 1.45,
        color: "text.secondary",
        "& > * + *": { mt: 1 },
      }}
    >
      {blocks.map((block, index) => renderBlock(block, index))}
    </Box>
  );
}
